import { z } from 'zod';
import { LocalizedStringSchema } from './studio';
import { LocalizedSeoSchema } from '../theme/themeSchemas';
import { OPEN_GRAPH_IMAGE_MODE } from '../theme/openGraphImage';

/**
 * Page routing + SEO metadata (scoped ProjectContent).
 */
export const PageMetaSchema = z.object({
  pageId: z.string().uuid(),
  slug: z
    .string()
    .min(1)
    .regex(/^[a-z0-9-_]+$/, { message: 'Slugs must be alphanumeric and URL-safe' }),
  localizedTitle: LocalizedStringSchema,
  seo: LocalizedSeoSchema,
  isSystemRoot: z.boolean().default(false),
  displayOrder: z.number().int(),
  /** ISO-8601; drives sitemap `<lastmod>` when present. */
  updatedAt: z.string().datetime().optional(),
});

export type PageMeta = z.infer<typeof PageMetaSchema>;

/** Incremental SEO field update for a project page (workspace). */
export const ProjectPageSeoPatchSchema = z.discriminatedUnion('field', [
  z.object({
    field: z.literal('title'),
    locale: z.string().min(2),
    value: z.string(),
  }),
  z.object({
    field: z.literal('description'),
    locale: z.string().min(2),
    value: z.string(),
  }),
  z.object({
    field: z.literal('openGraphImage'),
    value: z.string(),
  }),
  z.object({
    field: z.literal('openGraphImageMode'),
    value: z.enum([OPEN_GRAPH_IMAGE_MODE.auto, OPEN_GRAPH_IMAGE_MODE.custom]),
  }),
]);

export type ProjectPageSeoPatch = z.infer<typeof ProjectPageSeoPatchSchema>;
